import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Textarea, Toggle } from '@/components/ui'
import SuggestionBienField from '../biens/SuggestionField'

import { 
  createActivity, updateActivity, 
  resetActivityInEdition, 
  emptyActivity
} from './activitiesSlice'

import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'

export default ({ onClose = () => {}, onSuccess = () => {} }) => {
  const dispatch = useDispatch()
  const activity = useSelector( state => state.activities.inEdition || emptyActivity)
  const errors = useSelector((state) => state.activities.requestStatus.errors)
  const isCreating = useSelector((state) => state.activities.loadingStatus.createActivity)
  const isUpdating = useSelector((state) => state.activities.loadingStatus.updateActivity)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleSubmit, reset, getValues, setValue, control, watch, formState: { errors: formErrors } } = useForm({
    defaultValues: activity,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onReset = () => {
    dispatch(resetActivityInEdition())
    reset()
  }

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  useEffect(() => {
    if (activity) {
      Object.entries(activity).forEach(([key, value]) => {
        setValue(key, value)
      })
    }

    // useFormConfirmation feature
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)
  }, [activity, setValue])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        reset()
        dispatch(resetActivityInEdition())
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, onSuccess, dispatch])

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (activity.id) {
        await dispatch(updateActivity({id: activity.id, data: formData}))
      } else {
        await dispatch(createActivity({data: formData}))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }
  
  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {activity.id ? 'Modifier' : 'Ajouter'} une activité 
          </h2>
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 min-h-[300px] overflow-y-auto">
          <div className="grid grid-cols-3 gap-2 p-4">
            <div className='flex flex-col pl-2 space-y-4'>
              <Input.Date name='date_activity'
                label="Date"
                control={control}
              />
              <Controller 
                name='bien'
                control={control}
                defaultValues={""}
                rules={{
                  required: "Le bien doit être défini."
                }}
                render={({field}) => (
                  <SuggestionBienField
                    objectField={field}
                    originalData={activity.bien}
                    validator={{hasError, getErrorMessage}}
                  />
                )}
              />
            </div>
            <div className='flex flex-col pl-2 space-y-4'>
              <Textarea.Form name='details'
                label="Détails de l'activité"
                control={control}
                rules={{
                  required: "L'activité doit être décrite."
                }}
              />
            </div>
            <div className='flex flex-col pl-2 space-y-4'>
              <h3 className='font-bold'>Affichage :</h3>
              <Toggle.Form 
                control={control} 
                name="display_to_proprietaire" 
                label="Propriétaire" 
              />
              <Toggle.Form 
                control={control} 
                name="display_to_locataire" 
                label="Locataire" 
              />
              <Toggle.Form 
                control={control} 
                name="display_to_acquereur" 
                label="Acquéreur" 
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleFormCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2`}
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
        </div>
      </form>
      <FormConfirmDialog/>
    </div>
  )
}